import {Editor} from '@tinymce/tinymce-react';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import './AvadaTinyMCEEditor.css';
import {usePreloadAsset} from '../../hooks/usePreloadScript';

const TINY_SCRIPT_SRC = 'https://cdnapps.avada.io/tinymce/tinymce.min.js';
export const TINY_SCRIPT_URLS = [
  TINY_SCRIPT_SRC,
  'https://cdnapps.avada.io/tinymce/themes/silver/theme.min.js',
  'https://cdnapps.avada.io/tinymce/models/dom/model.min.js',
  'https://cdnapps.avada.io/tinymce/icons/default/icons.min.js',
  'https://cdnapps.avada.io/tinymce/icons/polaris/icons.js',
  'https://cdnapps.avada.io/tinymce/plugins/image/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/link/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/lists/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/media/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/table/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/wordcount/plugin.min.js',
  'https://cdnapps.avada.io/tinymce/plugins/code/plugin.min.js',
];
export const TINY_CSS_URLS = [
  'https://cdnapps.avada.io/tinymce/skins/ui/polaris/skin.min.css',
  'https://cdnapps.avada.io/tinymce/skins/ui/polaris/content.min.css',
  'https://cdnapps.avada.io/tinymce/skins/content/default/content.min.css',
];

export const usePreloadTinyMCE = () => {
  usePreloadAsset(
    TINY_CSS_URLS.map((url) => ({src: url})),
    'style'
  );

  usePreloadAsset(
    TINY_SCRIPT_URLS.map((url) => ({src: url})),
    'script'
  );
};

/**
 *
 * @param id
 * @param readOnly
 * @param focused
 * @param hasAction
 * @param discardFlag
 * @param onBlur
 * @param value
 * @param setValue
 * @param onInit
 * @returns {Element}
 * @constructor
 */
export default function AvadaTinyMCEEditor({
  id,
  readOnly = false,
  focused = false,
  disabled = false,
  discardFlag,
  onBlur = () => {},
  value,
  setValue,
  className = '',
  initHeight = 400,
}) {
  const [editorInstance, setEditorInstance] = useState(null);
  const [initValue, setInitValue] = useState(value);
  const valueRef = useRef(value);
  const [isFocused, setIsFocused] = useState(focused);

  const handleFocus = () => {
    if (!editorInstance) return;
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (!editorInstance) return;
    setIsFocused(false);
    onBlur();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitValue(valueRef.current);
    }, 1400);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!editorInstance) return;
    editorInstance.setContent(initValue);
  }, [discardFlag]);

  useEffect(() => {
    if (!editorInstance) return;
    if (focused) {
      editorInstance.focus();
    }
  }, [focused]);

  return (
    <Fragment>
      <div
        id={`${id}-avada-editor`}
        className={[
          className,
          isFocused && !disabled ? 'Avada__editor-focused' : '',
          disabled && 'Avada__editor-disabled',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Editor
          tinymceScriptSrc={TINY_SCRIPT_SRC}
          licenseKey={'gpl'}
          init={{
            height: initHeight,
            selector: 'textarea',
            skin: 'polaris',
            icons_url: 'https://cdnapps.avada.io/tinymce/icons/polaris/icons.js',
            skin_url: 'https://cdnapps.avada.io/tinymce/skins/ui/polaris',
            icons: 'polaris',
            menubar: false,
            plugins: ['image', 'link', 'lists', 'media', 'table', 'wordcount', 'code'],
            readonly: readOnly,
            resize: false,
            valid_elements: '*[*]',
            extended_valid_elements:
              'iframe[src|width|height|frameborder|allowfullscreen|style|class|allow],script[src|type],style[type],div[class|style]',
            valid_children: '+body[style|script|iframe],+div[style|script|iframe]',
            allow_script_urls: true,
            toolbar: `blocks | bold italic underline | align | customLink customImage customMedia | numlist bullist indent outdent table | emoticons charmap | removeformat | code fullscreen preview | image`,
            setup: (editor) => {
              setEditorInstance(editor);

              editor.on('PreInit', function () {
                editor.parser.addNodeFilter('iframe', function (nodes) {
                  nodes.forEach(function (node) {
                    node.attr('sandbox', null);
                  });
                });
              });
            },
          }}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={value}
          onNodeChange={() => {}}
          onEditorChange={(val) => {
            setValue(val);
          }}
          disabled={disabled}
        />
      </div>
    </Fragment>
  );
}
